/**
 * Plan and feature model for subscription management.
 * Four tiers: Basic (ad), Host (ad), Ad-free, Premium.
 * Features are gated by minimum required plan.
 */

export const PLAN_IDS = {
  /** Tier 1: Ad-supported. Participate in tournaments; schedule & team stats in own groups only. No leaderboard. */
  BASIC: 'basic',
  /** Tier 2: Ad-supported. Basic + host and manage tournaments they create. */
  HOST: 'host',
  /** Tier 3: Ad-free. All basic + host/manage own tournaments + leaderboard stats. */
  AD_FREE: 'ad_free',
  /** Tier 4: Premium. Manage teams, groups, tournaments; custom names, images, etc. */
  PREMIUM: 'premium',
};

/** Legacy id: treat as BASIC for existing users. */
export const PLAN_IDS_LEGACY = { FREE: 'free' };

/**
 * Display names, pricing (INR), and plan metadata.
 * Max plan (Premium) ₹500/year. Lower tiers (Host, Ad-free) are yearly-only; Premium offers monthly or yearly.
 */
export const PLANS = [
  {
    id: PLAN_IDS.BASIC,
    name: 'Basic',
    tagline: 'Ad-supported',
    description: 'Participate in tournaments. Schedule and team stats in your groups only. No leaderboard or global stats.',
    showAds: true,
    priceInrMonthly: 0,
    priceInrAnnual: 0,
    yearlyOnly: false,
  },
  {
    id: PLAN_IDS.HOST,
    name: 'Host',
    tagline: 'Ad-supported',
    description: 'Everything in Basic, plus host and manage tournaments you create.',
    showAds: true,
    priceInrMonthly: null,
    priceInrAnnual: 199,
    yearlyOnly: true,
  },
  {
    id: PLAN_IDS.AD_FREE,
    name: 'Ad-free',
    tagline: 'No ads',
    description: 'All basic features, host and manage your tournaments, plus leaderboard and global stats.',
    showAds: false,
    priceInrMonthly: null,
    priceInrAnnual: 349,
    yearlyOnly: true,
  },
  {
    id: PLAN_IDS.PREMIUM,
    name: 'Premium',
    tagline: 'Full control',
    description: 'Manage teams, groups, and tournaments you create. Custom tournament names, images, and more.',
    showAds: false,
    priceInrMonthly: 49,
    priceInrAnnual: 500,
    yearlyOnly: false,
  },
];

/** Feature keys used for gating. */
export const FEATURES = {
  HOME: 'home',
  DASHBOARD: 'dashboard',
  SCHEDULE: 'schedule',
  HOST_MATCH: 'host_match',
  GROUPS: 'groups',
  LEADERBOARD: 'leaderboard',
  PROFILE_ACTIVITY: 'profile_activity',
  TOURNAMENTS_LIST: 'tournaments_list',
  TOURNAMENT_REGISTER: 'tournament_register',
  TOURNAMENT_MANAGE: 'tournament_manage',
  TOURNAMENT_SCHEDULE: 'tournament_schedule',
  TOURNAMENT_DASHBOARD: 'tournament_dashboard',
  MATCH_HISTORY: 'match_history',
  PROFILE_EDIT: 'profile_edit',
  VENUES: 'venues',
  APARTMENT_MANAGE: 'apartment_manage',
  SELECT_GROUP: 'select_group',
  GROUP_MANAGE: 'group_manage',
  TOURNAMENT_CUSTOM: 'tournament_custom',
  SUBSCRIPTION: 'subscription',
  STATISTICS: 'statistics',
};

/** Human-readable labels and short descriptions for feature comparison. */
export const FEATURE_LABELS = {
  [FEATURES.HOME]: 'Home feed',
  [FEATURES.DASHBOARD]: 'Dashboard',
  [FEATURES.SCHEDULE]: 'Match schedule',
  [FEATURES.HOST_MATCH]: 'Host & score matches',
  [FEATURES.GROUPS]: 'View groups & teams',
  [FEATURES.LEADERBOARD]: 'Leaderboard & global stats',
  [FEATURES.PROFILE_ACTIVITY]: 'Profile & activity',
  [FEATURES.TOURNAMENTS_LIST]: 'Browse tournaments',
  [FEATURES.TOURNAMENT_REGISTER]: 'Register for tournaments',
  [FEATURES.TOURNAMENT_MANAGE]: 'Create & manage tournaments',
  [FEATURES.TOURNAMENT_SCHEDULE]: 'Tournament schedule',
  [FEATURES.TOURNAMENT_DASHBOARD]: 'Tournament dashboard',
  [FEATURES.MATCH_HISTORY]: 'Match history',
  [FEATURES.PROFILE_EDIT]: 'Edit profile',
  [FEATURES.VENUES]: 'Venues',
  [FEATURES.APARTMENT_MANAGE]: 'Apartment management',
  [FEATURES.SELECT_GROUP]: 'Select group',
  [FEATURES.GROUP_MANAGE]: 'Manage groups & teams',
  [FEATURES.TOURNAMENT_CUSTOM]: 'Custom names & images',
  [FEATURES.SUBSCRIPTION]: 'Plans & subscription',
  [FEATURES.STATISTICS]: 'Statistics hub',
};

/** Short description for each feature (for comparison / upgrade decision). */
export const FEATURE_DESCRIPTIONS = {
  [FEATURES.HOME]: 'See your feed and upcoming matches.',
  [FEATURES.DASHBOARD]: 'Overview of games, groups, and tournaments.',
  [FEATURES.SCHEDULE]: 'View and follow match schedules in your groups.',
  [FEATURES.HOST_MATCH]: 'Create matches and enter scores (host/organiser).',
  [FEATURES.GROUPS]: 'Join and view groups, teams, and members.',
  [FEATURES.LEADERBOARD]: 'Rankings and global stats across all players.',
  [FEATURES.PROFILE_ACTIVITY]: 'Your profile and activity across the app.',
  [FEATURES.TOURNAMENTS_LIST]: 'Discover and browse all tournaments.',
  [FEATURES.TOURNAMENT_REGISTER]: 'Register for tournaments you want to play.',
  [FEATURES.TOURNAMENT_MANAGE]: 'Create tournaments and manage draws, rounds, venues.',
  [FEATURES.TOURNAMENT_SCHEDULE]: 'View and manage tournament match schedule.',
  [FEATURES.TOURNAMENT_DASHBOARD]: 'Tournament overview, standings, and progress.',
  [FEATURES.MATCH_HISTORY]: 'Full history of your matches and results.',
  [FEATURES.PROFILE_EDIT]: 'Update display name, avatar, description, skill level.',
  [FEATURES.VENUES]: 'View and use venue and court information.',
  [FEATURES.APARTMENT_MANAGE]: 'Manage apartment and flat associations.',
  [FEATURES.SELECT_GROUP]: 'Switch between your groups.',
  [FEATURES.GROUP_MANAGE]: 'Create and manage groups, teams, logos, and members.',
  [FEATURES.TOURNAMENT_CUSTOM]: 'Custom tournament names, logos, and branding.',
  [FEATURES.SUBSCRIPTION]: 'View and manage your plan.',
  [FEATURES.STATISTICS]: 'Navigate to public permalinks (sport, player, tournament, match).',
};

/** Ordered feature keys for comparison table (grouped logically). */
export const FEATURE_COMPARISON_ORDER = [
  FEATURES.HOME,
  FEATURES.DASHBOARD,
  FEATURES.SCHEDULE,
  FEATURES.HOST_MATCH,
  FEATURES.GROUPS,
  FEATURES.LEADERBOARD,
  FEATURES.PROFILE_ACTIVITY,
  FEATURES.TOURNAMENTS_LIST,
  FEATURES.TOURNAMENT_REGISTER,
  FEATURES.TOURNAMENT_MANAGE,
  FEATURES.TOURNAMENT_SCHEDULE,
  FEATURES.TOURNAMENT_DASHBOARD,
  FEATURES.MATCH_HISTORY,
  FEATURES.PROFILE_EDIT,
  FEATURES.VENUES,
  FEATURES.APARTMENT_MANAGE,
  FEATURES.SELECT_GROUP,
  FEATURES.GROUP_MANAGE,
  FEATURES.TOURNAMENT_CUSTOM,
  FEATURES.SUBSCRIPTION,
  FEATURES.STATISTICS,
];

/** Route path -> feature key. */
export const ROUTE_TO_FEATURE = {
  '/': FEATURES.HOME,
  '/dashboard': FEATURES.DASHBOARD,
  '/schedule': FEATURES.SCHEDULE,
  '/host-match': FEATURES.HOST_MATCH,
  '/groups': FEATURES.GROUPS,
  '/leaderboard': FEATURES.LEADERBOARD,
  '/profile/activity': FEATURES.PROFILE_ACTIVITY,
  '/tournaments': FEATURES.TOURNAMENTS_LIST,
  '/tournament/register': FEATURES.TOURNAMENT_REGISTER,
  '/tournament/manage': FEATURES.TOURNAMENT_MANAGE,
  '/tournament/schedule': FEATURES.TOURNAMENT_SCHEDULE,
  '/tournament/dashboard': FEATURES.TOURNAMENT_DASHBOARD,
  '/profile/edit': FEATURES.PROFILE_EDIT,
  '/venues': FEATURES.VENUES,
  '/apartment-manage': FEATURES.APARTMENT_MANAGE,
  '/select-group': FEATURES.SELECT_GROUP,
  '/subscription': FEATURES.SUBSCRIPTION,
  '/statistics': FEATURES.STATISTICS,
};

/** Dynamic routes: path pattern -> feature key. */
export const ROUTE_PATTERN_TO_FEATURE = [
  { pattern: /^\/tournament\/[^/]+\/dashboard$/, feature: FEATURES.TOURNAMENT_DASHBOARD },
  { pattern: /^\/tournament\/[^/]+\/matches$/, feature: FEATURES.MATCH_HISTORY },
  { pattern: /^\/match\/[^/]+$/, feature: FEATURES.SCHEDULE },
];

/** Minimum plan required for each feature. Tournament playing/registered (list, register, dashboard, match history) are BASIC so they are always shown for any plan. */
export const FEATURE_REQUIRED_PLAN = {
  [FEATURES.HOME]: PLAN_IDS.BASIC,
  [FEATURES.DASHBOARD]: PLAN_IDS.BASIC,
  [FEATURES.SCHEDULE]: PLAN_IDS.BASIC,
  [FEATURES.HOST_MATCH]: PLAN_IDS.HOST,
  [FEATURES.GROUPS]: PLAN_IDS.BASIC,
  [FEATURES.LEADERBOARD]: PLAN_IDS.AD_FREE,
  [FEATURES.PROFILE_ACTIVITY]: PLAN_IDS.BASIC,
  [FEATURES.TOURNAMENTS_LIST]: PLAN_IDS.BASIC,
  [FEATURES.TOURNAMENT_REGISTER]: PLAN_IDS.BASIC,
  [FEATURES.TOURNAMENT_MANAGE]: PLAN_IDS.HOST,
  [FEATURES.TOURNAMENT_SCHEDULE]: PLAN_IDS.HOST,
  [FEATURES.TOURNAMENT_DASHBOARD]: PLAN_IDS.BASIC,
  [FEATURES.MATCH_HISTORY]: PLAN_IDS.BASIC,
  [FEATURES.PROFILE_EDIT]: PLAN_IDS.BASIC,
  [FEATURES.VENUES]: PLAN_IDS.BASIC,
  [FEATURES.APARTMENT_MANAGE]: PLAN_IDS.BASIC,
  [FEATURES.SELECT_GROUP]: PLAN_IDS.BASIC,
  [FEATURES.GROUP_MANAGE]: PLAN_IDS.PREMIUM,
  [FEATURES.TOURNAMENT_CUSTOM]: PLAN_IDS.PREMIUM,
  [FEATURES.SUBSCRIPTION]: PLAN_IDS.BASIC,
  [FEATURES.STATISTICS]: PLAN_IDS.AD_FREE,
};

const PLAN_ORDER = [PLAN_IDS.BASIC, PLAN_IDS.HOST, PLAN_IDS.AD_FREE, PLAN_IDS.PREMIUM];

/** Normalize legacy plan id (e.g. 'free') to current tier. */
export function normalizePlanId(planId) {
  if (!planId || planId === 'free') return PLAN_IDS.BASIC;
  if (PLAN_ORDER.includes(planId)) return planId;
  return PLAN_IDS.BASIC;
}

/** Compare plan tiers: returns true if planA has at least the same level as requiredPlan. */
export function planIncludes(planA, requiredPlan) {
  planA = normalizePlanId(planA);
  const a = PLAN_ORDER.indexOf(planA);
  const b = PLAN_ORDER.indexOf(requiredPlan);
  if (a < 0) return requiredPlan === PLAN_IDS.BASIC;
  if (b < 0) return true;
  return a >= b;
}

/** Get the minimum plan required for a feature. */
export function getRequiredPlanForFeature(featureKey) {
  return FEATURE_REQUIRED_PLAN[featureKey] || PLAN_IDS.BASIC;
}

/** Get feature key for a route path (exact or pattern). */
export function getFeatureKeyForPath(pathname) {
  const normalized = pathname?.replace(/\/$/, '') || '';
  if (ROUTE_TO_FEATURE[normalized] !== undefined) return ROUTE_TO_FEATURE[normalized];
  for (const { pattern, feature } of ROUTE_PATTERN_TO_FEATURE) {
    if (pattern.test(normalized)) return feature;
  }
  return null;
}

export function getPlanById(planId) {
  const id = normalizePlanId(planId);
  return PLANS.find(p => p.id === id) || PLANS[0];
}

/** Whether the given plan tier should see ads. */
export function planShowsAds(planId) {
  const plan = getPlanById(planId);
  return plan.showAds === true;
}
